import React, { useState, useMemo, useRef, useEffect } from 'react';
import { Search, X } from 'lucide-react';
import { useAvailableNodes } from '../../../../hooks/useAvailableNodes';
import { DynamicIcon } from './Nodes/DynamicIcon';
import * as Icons from 'lucide-react';

interface NodeContextMenuProps {
    position: { x: number; y: number } | null;
    onClose: () => void;
    onNodeAdd: (nodeType: string, nodeData?: Record<string, any>, position?: { x: number; y: number }) => void;
}

const NodeContextMenu: React.FC<NodeContextMenuProps> = ({ position, onClose, onNodeAdd }) => {
    const { nodes, loading } = useAvailableNodes();
    const [searchQuery, setSearchQuery] = useState('');
    const menuRef = useRef<HTMLDivElement>(null);
    const inputRef = useRef<HTMLInputElement>(null);

    // Filter nodes based on search query
    const filteredNodes = useMemo(() => {
        if (!searchQuery.trim()) {
            return nodes;
        }
        const query = searchQuery.toLowerCase();
        return nodes.filter(
            (node: any) =>
                node.default_name.toLowerCase().includes(query) ||
                node.description.toLowerCase().includes(query) ||
                node.ui_settings.category.toLowerCase().includes(query)
        );
    }, [searchQuery, nodes]);

    // Group nodes by category
    const categorizedNodes = useMemo(() => {
        const categories: Record<string, typeof nodes> = {};
        filteredNodes.forEach((node: any) => {
            const category = node.ui_settings.category || 'Other';
            if (!categories[category]) {
                categories[category] = [];
            }
            categories[category].push(node);
        });
        return categories;
    }, [filteredNodes]);

    // Focus input when menu opens
    useEffect(() => {
        if (position && inputRef.current) {
            inputRef.current.focus();
        }
    }, [position]);

    // Close menu when clicking outside
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
                onClose();
            }
        };

        const handleEscape = (event: KeyboardEvent) => {
            if (event.key === 'Escape') {
                onClose();
            }
        };

        if (position) {
            document.addEventListener('mousedown', handleClickOutside);
            document.addEventListener('keydown', handleEscape);
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
            document.removeEventListener('keydown', handleEscape);
        };
    }, [position, onClose]);

    if (!position) {
        return null;
    }

    const handleNodeClick = (node: any) => {
        onNodeAdd("custom", {
            id: node.id,
            label: node.default_name,
            description: "Click to edit",
            icon: node.ui_settings.icon,
            category: node.ui_settings.category,
        }, position);
        onClose();
    };

    return (
        <div
            ref={menuRef}
            className="fixed z-[200] rounded-lg border shadow-lg overflow-hidden bg-background border-border flex flex-col"
            style={{
                left: `${position.x}px`,
                top: `${position.y}px`,
                minWidth: '320px',
                maxWidth: '400px',
                maxHeight: '500px',
            }}
        >
            {/* Search Header */}
            <div className="p-3 border-b border-border">
                <div className="relative">
                    <Search
                        size={16}
                        className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground"
                    />
                    <input
                        ref={inputRef}
                        type="text"
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        placeholder="Search nodes..."
                        className="w-full pl-9 pr-8 py-2 rounded-md border outline-none bg-background border-muted-foreground text-foreground"
                    />
                    {searchQuery && (
                        <button
                            onClick={() => setSearchQuery('')}
                            className="absolute right-2 top-1/2 transform -translate-y-1/2 p-1 rounded hover:bg-muted/20 text-muted-foreground"
                        >
                            <X size={14} />
                        </button>
                    )}
                </div>
            </div>

            {/* Node List */}
            <div className="overflow-y-auto flex-1" style={{ maxHeight: '400px' }}>
                {loading ? (
                    <div className="p-4 text-center text-muted-foreground">
                        Loading nodes...
                    </div>
                ) : Object.keys(categorizedNodes).length === 0 ? (
                    <div className="p-4 text-center text-muted-foreground">
                        No nodes found
                    </div>
                ) : (
                    Object.entries(categorizedNodes).map(([category, categoryNodes]) => (
                        <div key={category}>
                            {/* Category Header */}
                            <div
                                className="px-3 py-2 text-xs font-semibold uppercase tracking-wider sticky top-0 bg-background text-muted-foreground border-b border-border"
                            >
                                {category}
                            </div>
                            {/* Category Nodes */}
                            {categoryNodes.map((node: any) => (
                                <button
                                    key={node.id}
                                    onClick={() => handleNodeClick(node)}
                                    className="w-full p-3 text-left transition-colors flex items-start gap-3 hover:bg-muted/10 text-foreground border-b border-border/20"
                                >
                                    <div className="mt-0.5 flex-shrink-0 text-primary">
                                        <DynamicIcon
                                            name={node.ui_settings.icon as keyof typeof Icons}
                                            size={16}
                                        />
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <div className="font-medium text-foreground">
                                            {node.default_name}
                                        </div>
                                        <div className="text-xs mt-1 text-muted-foreground">
                                            {node.description}
                                        </div>
                                    </div>
                                </button>
                            ))}
                        </div>
                    ))
                )}
            </div>
        </div>
    );
};

export default NodeContextMenu;

